import { ReportRoles, ROLES } from '../consts/roles'
import { type UserRole } from '../models/UserRoles'

/** CE-123 duplicate Viewer roles: `Viewer 1`, `Viewer 2`, … */
export const VIEWER_COPY_ROLE_PATTERN = /^Viewer (\d+)$/

export const isViewerCopyRole = (roleName: string | undefined): boolean =>
  roleName != null && VIEWER_COPY_ROLE_PATTERN.test(roleName)

/** Base `Viewer` plus CE-123 copies that inherit Viewer behavior. */
export const isViewerFamilyRole = (roleName: string | undefined): boolean =>
  roleName === ReportRoles.Viewer || isViewerCopyRole(roleName)

/** Owner still prefers Home. Viewer-family uses this only when Reports is not in the valid path set (CR-318). */
export const prefersHomeDashboardRole = (roleName: string | undefined): boolean =>
  roleName === ReportRoles.Owner || isViewerFamilyRole(roleName)

/** Viewer-family roles land on Reports when that page is in the valid path set (CR-318). */
export const prefersReportsLandingRole = (roleName: string | undefined): boolean =>
  isViewerFamilyRole(roleName)

export const isReportNavRole = (roleName: string | undefined): boolean =>
  roleName === ReportRoles.Owner || isViewerFamilyRole(roleName)

/**
 * Helper function to check if the current user has a specific role
 * @param currentRole - The current user's role
 * @param targetRole - The role to check against
 * @returns boolean indicating if the user has the target role
 */
export const hasRole = (currentRole: UserRole | undefined, targetRole: ROLES): boolean => {
  if (targetRole === ROLES.VIEWER) {
    return isViewerFamilyRole(currentRole?.roleName)
  }
  return currentRole?.roleName === targetRole
}

/**
 * Helper function to check if the current user has any of the specified roles
 * @param currentRole - The current user's role
 * @param targetRoles - Array of roles to check against
 * @returns boolean indicating if the user has any of the target roles
 */
export const hasAnyRole = (currentRole: UserRole | undefined, targetRoles: ROLES[]): boolean => {
  if (!currentRole) return false
  return targetRoles.some(role => hasRole(currentRole, role))
}

/**
 * Helper function to get the current role from localStorage and user roles
 * @param currentUserRoles - Array of user roles
 * @returns The current role or undefined
 */
export const getCurrentRole = (currentUserRoles: UserRole[]): UserRole | undefined => {
  const currentRoleId = localStorage.getItem('currentRoleId')
  return currentUserRoles.find(role => role.roleId === currentRoleId)
}

export interface RoleAssignmentResult {
  /**
   * BE returned 200 with `already_exists: true` — the user already held this
   * role for the same academic unit/section with the same (or no) attributes,
   * so the call was an idempotent no-op rather than a fresh create (201).
   */
  alreadyExists: boolean
  /** Informational BE message to surface when the assignment already existed. */
  message?: string
}

/**
 * Interprets the body of a successful role-assignment response
 * (POST /users/roles/ or POST /users/<user_id>/role/).
 *
 * A fresh assignment returns 201 with the plain role-assignment payload. A
 * duplicate assignment (same user + role + academic unit/section, with no or
 * identical attributes) now returns 200 with an additive `alreadyExists` flag
 * and a human-readable `message`. Callers should treat the latter as success
 * but show an informational notice instead of a "created" success.
 *
 * The wire fields `already_exists`/`message` arrive camelCased via the shared
 * client's case-converter. Conflicting (409) and validation (400) responses are
 * rejected by the shared client and handled through the normal error path, not
 * here.
 */
export const interpretRoleAssignment = (data: unknown): RoleAssignmentResult => {
  const body = (data ?? {}) as Record<string, unknown>
  const alreadyExists = body.alreadyExists === true
  const message = typeof body.message === 'string' && body.message.trim().length > 0
    ? body.message
    : undefined
  return {
    alreadyExists,
    message: alreadyExists ? message : undefined
  }
}
